import React, { useMemo, useState, Component } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, FilterIcon, BarChart3Icon, TableIcon, PieChartIcon } from 'lucide-react';
import { getReport, ChartType } from '../data/reports';
import { runReport } from '../utils/reportEngine';
import { ReportTable } from '../components/reports/ReportTable';
import { ReportChart } from '../components/reports/ReportChart';
import { ExportButton } from '../components/reports/ExportButton';
const viewOptions: {
  value: ChartType;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
}[] = [{
  value: 'table',
  label: 'Table',
  icon: TableIcon
}, {
  value: 'bar',
  label: 'Bar',
  icon: BarChart3Icon
}, {
  value: 'pie',
  label: 'Pie',
  icon: PieChartIcon
}];
export function ReportViewer() {
  const {
    reportId
  } = useParams<{
    reportId: string;
  }>();
  const report = reportId ? getReport(reportId) : undefined;
  const [filterValue, setFilterValue] = useState('');
  const [view, setView] = useState<ChartType | null>(null);
  const result = useMemo(() => {
    if (!report) return null;
    const config = filterValue ? {
      ...report.config,
      filters: [...report.config.filters, {
        field: 'company',
        operator: 'contains' as const,
        value: filterValue
      }]
    } : report.config;
    return runReport(config);
  }, [report, filterValue]);
  if (!report || !result) {
    return <div className="mx-auto w-full max-w-3xl px-4 py-16 text-center" data-id="element-360">
        <p className="text-sm font-medium text-navy-900" data-id="element-361">Report not found</p>
        <Link to="/reports" className="mt-2 inline-block text-sm text-accent-700 hover:underline" data-id="element-362">
          Back to Report Library
        </Link>
      </div>;
  }
  const activeView = view ?? report.config.chartType;
  const canChart = result.grouped !== null;
  return <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8" data-id="element-363">
      <Link to="/reports" className="inline-flex items-center gap-1 text-xs font-medium text-navy-400 transition-colors hover:text-navy-900" data-id="element-364">
        <ChevronLeftIcon className="h-3.5 w-3.5" data-id="element-365" /> Report Library
      </Link>

      <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-366">
        <div data-id="element-367">
          <h2 className="text-xl font-semibold tracking-tight text-navy-900" data-id="element-368">
            {report.name}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-navy-500" data-id="element-369">
            {report.description}
          </p>
        </div>
        <ExportButton reportName={report.name} rowCount={result.rows.length} data-id="element-370" />
      </div>

      <div className="card-float mt-6 flex flex-col gap-3 rounded-2xl bg-white p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-371">
        <div className="flex items-center gap-2" data-id="element-372">
          <FilterIcon className="h-4 w-4 text-navy-300" data-id="element-373" />
          <label htmlFor="company-filter" className="text-xs font-medium text-navy-600" data-id="element-374">
            Company
          </label>
          <input id="company-filter" type="text" value={filterValue} onChange={e => setFilterValue(e.target.value)} placeholder="All companies" className="h-8 w-48 rounded-full border border-navy-100 bg-navy-50/50 px-3 text-sm text-navy-900 placeholder:text-navy-400 focus:border-navy-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-navy-900/10" data-id="element-375" />
          <span className="ml-1 text-xs text-navy-300" data-id="element-376">
            {result.rows.length} record{result.rows.length === 1 ? '' : 's'}
          </span>
        </div>
        {canChart && <div className="flex rounded-full bg-navy-50 p-0.5" role="tablist" aria-label="View type" data-id="element-377">
            {viewOptions.map(opt => <button key={opt.value} type="button" role="tab" aria-selected={activeView === opt.value} onClick={() => setView(opt.value)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${activeView === opt.value ? 'bg-navy-900 text-white' : 'text-navy-500 hover:text-navy-900'}`} data-id="element-378">
                <opt.icon className="h-3.5 w-3.5" data-id="element-379" />
                {opt.label}
              </button>)}
          </div>}
      </div>

      <motion.div key={activeView + filterValue} initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} className="card-float mt-4 overflow-hidden rounded-2xl bg-white" data-id="element-380">
        {activeView === 'table' || !canChart ? <ReportTable columns={result.grouped && activeView === 'table' ? [{
        key: 'group',
        label: report.config.groupBy ?? 'Group'
      }, {
        key: 'value',
        label: result.metricLabel
      }] : result.columns} rows={result.grouped && activeView === 'table' ? result.grouped.map(g => ({
        group: g.group,
        value: g.value
      })) : result.rows} data-id="element-381" /> : <div className="p-4" data-id="element-382">
            <ReportChart data={result.grouped!} chartType={activeView} metricLabel={result.metricLabel} data-id="element-383" />
          </div>}
      </motion.div>
    </div>;
}